import { useCallback, useEffect } from 'react';
import type { MouseEvent } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { FeedType } from './useFeedTypeSelector.ts';

type UseFeedTypeSelectionAnalyticsOptions = {
	feedType: FeedType | string;
	selectedFeedType: FeedType | string;
	setFeedType: (newFeedType: FeedType | string) => void;
	isPulseActive: boolean;
	onPulseClick: () => void;
};

export const useFeedTypeSelectionAnalytics = ({
	feedType,
	selectedFeedType,
	setFeedType,
	isPulseActive,
	onPulseClick,
}: UseFeedTypeSelectionAnalyticsOptions): {
	handleFeedTypeClick: () => void;
	handleMouseDown: (event: MouseEvent<HTMLElement>) => void;
	handlePulseClick: () => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleFeedTypeClick = useCallback((): void => {
		if (selectedFeedType !== feedType) {
			setFeedType(feedType);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'feedTypeSelector',
					actionSubjectId: feedType.toLowerCase(),
					source: 'homeScreen',
					attributes: {
						homeVersion: 'visualRefresh',
					},
				},
			}).fire();
		}
	}, [feedType, selectedFeedType, setFeedType, createAnalyticsEvent]);

	const handleMouseDown = useCallback((event: MouseEvent<HTMLElement>): void => {
		// prevent focus
		event.preventDefault();
	}, []);

	useEffect(() => {
		if (isPulseActive) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'feedTypeSelectorPulse',
					attributes: {
						feedType: feedType.toLowerCase(),
					},
				},
			}).fire();
		}
	}, [isPulseActive, feedType, createAnalyticsEvent]);

	const handlePulseClick = useCallback((): void => {
		if (isPulseActive) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'feedTypeSelectorPulse',
					attributes: {
						feedType: feedType.toLowerCase(),
						homeVersion: 'visualRefresh',
					},
					source: 'homeScreen',
				},
			}).fire();
			onPulseClick();
		}
	}, [feedType, isPulseActive, onPulseClick, createAnalyticsEvent]);

	return {
		handleFeedTypeClick,
		handleMouseDown,
		handlePulseClick,
	};
};
